import {Renderer, RendererProps} from 'amis';
import EmbedPDF from '@embedpdf/snippet';
import React, {useState, useEffect, useRef, useMemo} from 'react';
import {getVariable, resolveVariableAndFilter, isPureVariable} from 'amis-core';

export interface PdfViewerPluginProps extends RendererProps {
  /** PDF 文件链接或 Base64（可以是表达式） */
  src?: string;
  /** 文件来源类型 */
  source?: 'src' | 'file';
  /** 容器高度 */
  height?: string | number;
  /** 容器宽度 */
  width?: string | number;
  className?: string;
  /** 编辑器里上传用的内部字段 */
  __file?: {
    url?: string;
    value?: string;
    [key: string]: any;
  } | null;
}

interface PdfViewerState {
  readonly isLoading: boolean;
  readonly error: string | null;
}

interface EmbedPdfViewer {
  readonly registry: Promise<unknown>;
  destroy?: () => void;
}

interface DocumentManagerProvides {
  openDocumentUrl: (args: {
    readonly url: string;
    readonly documentId?: string;
    readonly autoActivate?: boolean;
  }) => void | Promise<void>;
  openDocumentBuffer: (args: {
    readonly buffer: ArrayBuffer;
    readonly name?: string;
    readonly documentId?: string;
    readonly autoActivate?: boolean;
  }) => void | Promise<void>;
}

interface RegistryLike {
  getPlugin: (name: 'document-manager') => {provides: () => DocumentManagerProvides};
}

const isBlobUrl = (value: string): boolean => value.startsWith('blob:');

const guessFileNameFromSrc = (value: string): string => {
  const last = value.split('/').pop();
  return (last && last.trim()) ? last : 'document.pdf';
};

/**
 * 内部展示组件：只关心最终的 src，不做变量解析
 */
export const InnerPdfViewer: React.FC<{
  src?: string;
  height?: string | number;
  width?: string | number;
  className?: string;
}> = props => {
  const {src, height = '100%', width = '100%', className = ''} = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<EmbedPdfViewer | null>(null);

  const [state, setState] = useState<PdfViewerState>({
    isLoading: false,
    error: null
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let isMounted = true;

    const initViewer = async () => {
      try {
        setState(prev => ({...prev, isLoading: true, error: null}));
        container.innerHTML = '';

        const locales =
          (window as any).AGIMX_CONFIG?.locales === 'en-US' ? 'en' : 'zh-CN';

        const viewer = EmbedPDF.init({
          type: 'container',
          target: container,
          theme: {preference: 'system'},
          i18n: {defaultLocale: locales}
        }) as unknown as EmbedPdfViewer;

        viewerRef.current = viewer;

        if (isMounted) {
          setState(prev => ({...prev, isLoading: false}));
        }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : '初始化 PDF 预览失败';
        console.error('[PdfViewer] init failed:', {message, err});
        if (isMounted) {
          setState(prev => ({...prev, isLoading: false, error: message}));
        }
      }
    };

    void initViewer();

    return () => {
      isMounted = false;
      const viewer = viewerRef.current;
      try {
        viewer?.destroy?.();
      } catch (err) {
        console.error('[PdfViewer] destroy failed:', err);
      } finally {
        viewerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const openDocument = async () => {
      if (!src) {
        setState(prev => ({...prev, isLoading: false}));
        return;
      }

      // const viewer = viewerRef.current;
      // if (!viewer) {
      //   return;
      // }

      const container = containerRef.current;
      if (!container) {
        return;
      }

      try {
        setState(prev => ({...prev, isLoading: true, error: null}));

        // 1. 每次 src 变化时，销毁旧的 viewer，确保没有历史 tab
        const oldViewer = viewerRef.current;
        if (oldViewer) {
          try {
            oldViewer.destroy?.();
          } catch (destroyErr) {
            console.error('[PdfViewer] destroy previous viewer failed:', destroyErr);
          } finally {
            viewerRef.current = null;
          }
        }

        // 2. 清空容器
        container.innerHTML = '';

        // 3. 重新初始化新的 viewer
        const locales =
          (window as any).AGIMX_CONFIG?.locales === 'en-US' ? 'en' : 'zh-CN';

        const newViewer = EmbedPDF.init({
          type: 'container',
          target: container,
          theme: {preference: 'system'},
          i18n: {defaultLocale: locales}
        }) as unknown as EmbedPdfViewer;

        viewerRef.current = newViewer;

        const registry = (await newViewer.registry) as unknown as RegistryLike;
        const docManager = registry.getPlugin('document-manager').provides();

        // 4. 打开当前 src 对应的文档
        if (isBlobUrl(src)) {
          const response = await fetch(src);
          if (!response.ok) {
            throw new Error(`读取本地文件失败（${response.status}）`);
          }
          const buffer = await response.arrayBuffer();

          await docManager.openDocumentBuffer({
            buffer,
            name: guessFileNameFromSrc(src),
            autoActivate: true
          });
        } else {
          await docManager.openDocumentUrl({
            url: src,
            autoActivate: true
          });
        }

        if (isMounted) {
          setState(prev => ({...prev, isLoading: false}));
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'PDF 加载失败';
        console.error('[PdfViewer] open document failed:', {src, message, err});
        if (isMounted) {
          setState(prev => ({...prev, isLoading: false, error: message}));
        }
      }
    };

    void openDocument();

    return () => {
      isMounted = false;
    };
  }, [src]);

  const containerStyle: React.CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
    position: 'relative',
    backgroundColor: '#ffffff',
    borderRadius: '4px',
    overflow: 'hidden'
  };

  const messageStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#cbd5e1',
    fontSize: '14px'
  };

  const errorStyle: React.CSSProperties = {
    ...messageStyle,
    color: '#fb7185',
    flexDirection: 'column',
    gap: '8px',
    padding: '12px'
  };

  if (state.error) {
    return (
      <div className={className} style={containerStyle}>
        <div style={errorStyle}>
          <span>PDF 预览失败：{state.error}</span>
        </div>
      </div>
    );
  }

  if (!src) {
    return (
      <div className={className} style={containerStyle}>
        <div style={messageStyle}>
          <span>请配置 PDF 文件地址</span>
        </div>
      </div>
    );
  }

  return (
    <div className={className} style={containerStyle}>
      {state.isLoading && (
        <div
          style={{
            ...messageStyle,
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            background: 'rgba(2,6,23,0.45)'
          }}
        >
          <span>加载中...</span>
        </div>
      )}
      <div ref={containerRef} style={{width: '100%', height: '100%'}} />
    </div>
  );
};

/**
 * 容器渲染器：对接 amis 的数据和表达式，再把 realSrc 传给 InnerPdfViewer
 */
const PdfViewerContainer: React.FC<PdfViewerPluginProps> = props => {
  const {
    data,
    src,
    source = 'src',
    __file,
    height,
    width,
    className
  } = props;

  const realSrc = useMemo(() => {
    // 1）优先按照 source 处理
    if (source === 'file' && __file) {
      if (typeof __file === 'object') {
        if (typeof __file.url === 'string' && __file.url) {
          return __file.url;
        }
        if (typeof __file.value === 'string' && __file.value) {
          return __file.value;
        }
      }
    }

    // 2）回退到 src
    if (!src) {
      return undefined;
    }
    
    // 支持纯表达式：${xxx}
    if (isPureVariable(src)) {
      const value = resolveVariableAndFilter(src, data, '| raw');
      if (typeof value === 'string') {
        return value;
      }
      if (value && typeof value === 'object' && typeof value.url === 'string') {
        return value.url;
      }
      return undefined;
    }
    
    

    // 非纯表达式时，依然尝试做一轮 filter 解析
    const resolved = resolveVariableAndFilter(src, data, '| raw');
    
    if (typeof resolved === 'string') {
      return resolved;
    }
    if (resolved && typeof resolved === 'object' && typeof resolved.url === 'string') {
      return resolved.url;
    }

    return src;
  }, [src, source, __file, data]);

  return (
    <InnerPdfViewer
      src={realSrc}
      height={height}
      width={width}
      className={className}
    />
  );
};

@Renderer({
  test: /\bagimx-pdf-viewer$/,
  name: 'agimx-pdf-viewer'
})
export default class PdfViewerPlugin extends React.Component<PdfViewerPluginProps> {
  static defaultProps: Partial<PdfViewerPluginProps> = {
    source: 'src',
    height: '100%',
    width: '100%'
  };

  render() {
    return <PdfViewerContainer {...this.props} />;
  }
}